import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, Search } from 'lucide-react';
import api from '../../../utils/authUtils';
import {
  Container, BackLink, Header, Title, Subtitle, Card, CardTitle, FormGrid, Field, WideField,
  Input, Actions, Button, Message, WORKSPACE,
} from './ui';

const normalize = name => name.trim().toLowerCase().replace(/\s+/g, ' ');

// The only entry point that starts a brand-new AI research job. Editing an
// existing profile's researched fields, or inviting a known speaker to
// another event, both happen on the list/detail pages - never here - so
// this form is never mistaken for "find this speaker again".
const SpeakerResearch = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [draft, setDraft] = useState({ fullName: '', title: '', institution: '', city: '', country: '' });
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [dismissedWarning, setDismissedWarning] = useState(false);

  useEffect(() => {
    api.get('/api/cms/speaker-profiles').then(({ data }) => setProfiles(data || [])).catch(() => {});
  }, []);

  const matches = draft.fullName.trim().length > 1
    ? profiles.filter(p => normalize(p.fullName) === normalize(draft.fullName))
    : [];

  const create = async e => {
    e.preventDefault(); setBusy(true); setMessage(null);
    try {
      const { data } = await api.post('/api/cms/speaker-profiles', { ...draft, professionalStatuses: ['SPEAKER'], interests: [] });
      navigate(`${WORKSPACE}/${data.id}`);
    } catch (err) { setMessage({ error: true, text: err.response?.data || t('speakerMgmt.msg.createFailed', 'Could not create draft.') }); }
    finally { setBusy(false); }
  };

  return (
    <Container style={{ maxWidth: '48rem' }}>
      <BackLink to={WORKSPACE}><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.backList" defaultMessage="Back to speakers" /></BackLink>
      <Header><Title><FormattedMessage id="speakerMgmt.research.title" defaultMessage="Research a new speaker" /></Title></Header>
      <Subtitle><FormattedMessage id="speakerMgmt.subtitle" defaultMessage="Create and review an evidence-backed profile before sending any invitation." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}
      <Card>
        <CardTitle><FormattedMessage id="speakerMgmt.newDraft" defaultMessage="New speaker draft" /></CardTitle>
        <form onSubmit={create}>
          <FormGrid>
            <Field><span><FormattedMessage id="speaker.field.name" defaultMessage="Name" /> *</span><Input required autoFocus value={draft.fullName} onChange={e => { setDraft({ ...draft, fullName: e.target.value }); setDismissedWarning(false); }} /></Field>
            <Field><FormattedMessage id="speakerMgmt.hint.title" defaultMessage="Academic title hint" /><Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} /></Field>
            <WideField><FormattedMessage id="speakerMgmt.hint.institution" defaultMessage="Institution hint" /><Input value={draft.institution} onChange={e => setDraft({ ...draft, institution: e.target.value })} /></WideField>
            <Field><FormattedMessage id="speakerMgmt.hint.city" defaultMessage="City hint" /><Input value={draft.city} onChange={e => setDraft({ ...draft, city: e.target.value })} /></Field>
            <Field><FormattedMessage id="speakerMgmt.hint.country" defaultMessage="Country hint" /><Input value={draft.country} onChange={e => setDraft({ ...draft, country: e.target.value })} /></Field>
          </FormGrid>
          {matches.length > 0 && !dismissedWarning && (
            <Message warn style={{ marginTop: '.9rem' }}>
              <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '.35rem' }} />
              <FormattedMessage id="speakerMgmt.research.duplicateWarning" defaultMessage="{name} already exists in the system. Starting a new AI research here creates a separate, unlinked profile and repeats a paid research job."
                values={{ name: <strong>{draft.fullName.trim()}</strong> }} />
              <ul style={{ margin: '.5rem 0 0', paddingLeft: '1.1rem' }}>
                {matches.map(m => <li key={m.id}><Link to={`${WORKSPACE}/${m.id}`}>{m.fullName}{m.email ? ` — ${m.email}` : ''}</Link></li>)}
              </ul>
              <Actions style={{ marginTop: '.6rem' }}>
                <Button secondary type="button" onClick={() => setDismissedWarning(true)}><FormattedMessage id="speakerMgmt.research.duplicateProceed" defaultMessage="It's a different person, continue anyway" /></Button>
              </Actions>
            </Message>
          )}
          <Actions><Button disabled={busy}><Search size={16} /><FormattedMessage id="speakerMgmt.createResearch" defaultMessage="Start AI research" /></Button></Actions>
        </form>
      </Card>
    </Container>
  );
};

export default SpeakerResearch;
